import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./components/Auth/AuthProvider";
import { Navigation } from "./components/Navigation/Navigation";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut } from "firebase/auth";
import { doc, setDoc, getDoc, collection, getDocs, getDocsFromServer, query, orderBy, limit } from "firebase/firestore";
import { auth, db, getMessagingSafe, canUseMessaging } from "./firebase";
import './App.css';
/* Estilos del CRT (mismas clases que WelcomePage) para overlay global al cerrar sesión */
import './components/WelcomePage.css';

// ✅ NUEVO: Importar contexto de sponsors y caché de dietas/rutinas
import { SponsorProvider } from "./contexts/SponsorContext";
import { AIAssistantProvider } from "./contexts/AIAssistantContext";
import { DietsRoutinesCacheProvider } from "./contexts/DietsRoutinesCacheContext";
import FitnessProfileBannerModal, { getFitnessBannerDismissed } from "./components/FitnessProfileBannerModal/FitnessProfileBannerModal";
import { syncAllPendingWorkouts } from "./utils/syncWorkoutPending";
import { buildPublicAppUrl } from "./utils/publicAppUrl";
import { RATAFIT_CONTACT_EMAIL } from "./utils/contactEmail";
// Spinner component
import { LoadingSpinner } from "./components/Spinner";
import lazyWithRetry from "./utils/lazyWithRetry";

// Carga diferida de componentes no críticos para arranque más rápido
const AIAssistant = lazyWithRetry(() => import("./components/AIAssistant/AIAssistant"), "AIAssistant");
const BlockedUserScreen = lazyWithRetry(() => import("./components/BlockedUserScreen"), "BlockedUserScreen");
const SponsorCubeOptimized = lazyWithRetry(() => import("./components/SponsorCubeOptimized"), "SponsorCubeOptimized");
const InstallBanner = lazyWithRetry(() => import("./components/InstallBanner"), "InstallBanner");

// Componentes principales
const RoutineManager = lazyWithRetry(() => import("./components/RoutineManager"), "RoutineManager");
const DietManager = lazyWithRetry(() => import("./components/DietManager"), "DietManager");
const Premium = lazyWithRetry(() => import("./components/Premium"), "Premium");
const UserProfile = lazyWithRetry(() => import("./components/UserProfile"), "UserProfile");
const UserSearch = lazyWithRetry(() => import("./components/UserSearch"), "UserSearch");
const ProgressTracker = lazyWithRetry(() => import("./components/ProgressTracker"), "ProgressTracker");
const WelcomePage = lazyWithRetry(() => import("./components/WelcomePage"), "WelcomePage");
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'), "AdminDashboard");
const PoliticaDePrivacidad = lazyWithRetry(() => import('./components/PoliticaDePrivacidad'), "PoliticaDePrivacidad");
const AuthAppPage = lazyWithRetry(() => import('./components/Auth/AuthAppPage'), "AuthAppPage");

// ✅ NUEVO: Hook personalizado para manejar el avatar del usuario
const useUserAvatar = (user, userProfileData) => {
  const [avatarState, setAvatarState] = useState({
    url: "/default-avatar.png",
    isCustom: false,
    loading: true
  });

  useEffect(() => {
    if (!user) {
      setAvatarState({
        url: "/default-avatar.png",
        isCustom: false,
        loading: false
      });
      return;
    }

    // Prioridad: customPhotoURL > photoURL > default
    const customUrl = userProfileData?.customPhotoURL;
    const googleUrl = user?.photoURL;

    if (customUrl) {
      setAvatarState({
        url: customUrl,
        isCustom: true,
        loading: false
      });
    } else if (googleUrl) {
      setAvatarState({
        url: googleUrl,
        isCustom: false,
        loading: false
      });
    } else {
      setAvatarState({
        url: "/default-avatar.png",
        isCustom: false,
        loading: false
      });
    }
  }, [user, userProfileData?.customPhotoURL]);

  return avatarState;
};

function App() {
  const [user, loading, error] = useAuthState(auth);
  const {
    signInWithGoogle: contextSignInWithGoogle,
    signInWithGoogleForWelcome: contextSignInWithGoogleForWelcome,
    signInWithGooglePopup,
    loginInProgress
  } = useAuth();

  // Estados principales
  const [currentView, setCurrentView] = useState("welcome");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRoutineId, setSelectedRoutineId] = useState(null);
  const [selectedDayName, setSelectedDayName] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [sharedProfileUserId, setSharedProfileUserId] = useState(null);
  const [sharedRoutineFromLinkId, setSharedRoutineFromLinkId] = useState(null);
  const [sharedDietFromLinkId, setSharedDietFromLinkId] = useState(null);
  const [routineManagerKey, setRoutineManagerKey] = useState(0);
  const [scrollToRoutineId, setScrollToRoutineId] = useState(null);
  const [continueData, setContinueData] = useState({ hasActiveWorkout: false, destination: null });
  const [welcomeContinueRefreshTrigger, setWelcomeContinueRefreshTrigger] = useState(0);
  const [dietManagerKey, setDietManagerKey] = useState(0);
  const [scrollToDietId, setScrollToDietId] = useState(null);
  const [routinesGoToRootTrigger, setRoutinesGoToRootTrigger] = useState(0);
  const [dietsGoToRootTrigger, setDietsGoToRootTrigger] = useState(0);

  // Asistente IA global
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiAssistantMinimizedWhileLoading, setAIAssistantMinimizedWhileLoading] = useState(false);

  // Estados de UI
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // ✅ NUEVO: Estado para controlar la inicialización completa
  const [isInitialized, setIsInitialized] = useState(false);

  // ✅ NUEVO: Estado para verificar si el usuario es admin
  const [isAdmin, setIsAdmin] = useState(false);

  // ✅ NUEVO: Estado para verificar si el usuario está bloqueado
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [blockInfo, setBlockInfo] = useState(null);

  // Modal de perfil físico para el asistente IA (se muestra a los 5s si no tiene datos)
  const [showFitnessBanner, setShowFitnessBanner] = useState(false);

  const dropdownRef = useRef(null);
  const initializationRef = useRef(false);
  const signOutCrtAnimHandledRef = useRef(false);

  /** idle | animating | signingOut — animación CRT a pantalla completa y luego signOut */
  const [signOutCrtPhase, setSignOutCrtPhase] = useState('idle');
  const [signOutCrtSurfaceKey, setSignOutCrtSurfaceKey] = useState(0);

  // ✅ NUEVO: Hook para manejar avatar (estado interno del hook; Navigation usa su propio AvatarImage)
  useUserAvatar(user, userProfileData);

  // ✅ NUEVO: Función para verificar si el usuario está bloqueado
  const checkUserBlockStatus = useCallback(async () => {
    if (!user) {
      setIsUserBlocked(false);
      setBlockInfo(null);
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const isBlocked = userData.isBlocked || false;

        if (isBlocked) {
          // Verificar si es un bloqueo temporal que ya expiró
          const blockedUntil = userData.blockedUntil;
          let isBlockActive = true;

          if (blockedUntil) {
            const expirationDate = blockedUntil.toDate ? blockedUntil.toDate() : new Date(blockedUntil);
            isBlockActive = new Date() < expirationDate;
          }

          setIsUserBlocked(isBlockActive);

          if (isBlockActive) {
            setBlockInfo({
              reason: userData.blockedReason || 'Violación de términos de servicio',
              blockedAt: userData.blockedAt,
              blockedUntil: userData.blockedUntil,
              isPermanent: !userData.blockedUntil,
              blockedBy: userData.blockedBy
            });
          } else {
            // El bloqueo temporal ha expirado, actualizarlo en la base de datos
            await setDoc(userRef, {
              isBlocked: false,
              blockedReason: null,
              blockedUntil: null
            }, { merge: true });

            setBlockInfo(null);
          }
        } else {
          setIsUserBlocked(false);
          setBlockInfo(null);
        }
      }
    } catch (error) {
      console.error('Error verificando estado de bloqueo:', error);
      setIsUserBlocked(false);
      setBlockInfo(null);
    }
  }, [user]);

  // ✅ NUEVO: Función para verificar si el usuario es admin
  const checkAdminStatus = useCallback(async () => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const isAdminUser = userData.isAdmin === true;
        setIsAdmin(isAdminUser);

        // console.log('Admin status checked:', { email: user.email, isAdmin: isAdminUser });
      } else {
        // Fallback para verificación por email si no hay documento
        const adminEmails = [RATAFIT_CONTACT_EMAIL];
        const isAdminUser = adminEmails.includes(user.email);
        setIsAdmin(isAdminUser);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  }, [user]);

  // ✅ OPTIMIZACIÓN: Manejar routing de manera más eficiente con ruta admin
  const handleRouting = useCallback(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    // Ruta para login desde app móvil (navegador → Google → ratafit://auth#token)
    if (path === '/auth-app') {
      setCurrentView('authApp');
      return;
    }

    // Ruta política de privacidad (aceptar variantes de URL)
    if (
      path === '/politica-de-privacidad' ||
      path === '/politicas-de-privacidad' ||  // variante plural
      path === '/privacy-policy'              // variante en inglés por si se enlaza así
    ) {
      setCurrentView('politica');
      return;
    }

    // ✅ NUEVO: Manejar ruta /admin
    if (path === '/admin') {
      if (user) {
        setCurrentView("admin");
        return;
      } else {
        // Redirigir a welcome si no está logueado
        setCurrentView("welcome");
        window.history.pushState({}, '', '/');
        return;
      }
    }

    // Rutas directas /premium, /routines, /diets, /search, /progress (evitan 404 en Hosting)
    if (path === '/premium' || path === '/routines' || path === '/diets' || path === '/search' || path === '/progress') {
      if (user) {
        const viewMap = { '/premium': 'premium', '/routines': 'routines', '/diets': 'diets', '/search': 'search', '/progress': 'progress' };
        setCurrentView(viewMap[path]);
        if (path === '/routines') setRoutineManagerKey(prev => prev + 1);
        return;
      }
      setCurrentView("welcome");
      return;
    }

    // ✅ NUEVO: Ruta raíz va a welcome
    if (path === '/' && !urlParams.toString()) {
      setCurrentView("welcome");
      return;
    }

    // ✅ HÍBRIDO: Manejar rutas /profile/userId o ?profile=userId
    if (path.startsWith('/profile/')) {
      const userId = path.split('/profile/')[1];
      if (userId) {
        const routineIdParam = urlParams.get('routine');
        const dietIdParam = urlParams.get('diet');
        setSharedRoutineFromLinkId(routineIdParam || null);
        setSharedDietFromLinkId(dietIdParam || null);
        if (user) {
          // Usuario logueado: mostrar perfil completo en la app
          setSelectedUserId(userId);
          setCurrentView("profile");
          setSharedProfileUserId(null); // Limpiar estado compartido
        } else {
          // Usuario no logueado: UserProfile mostrará pantalla de registro
          setSelectedUserId(userId);
          setCurrentView("profile");
          setSharedProfileUserId(userId); // Mantener para después del login
        }
      }
    } else if (urlParams.get('profile')) {
      const userId = urlParams.get('profile');
      if (userId) {
        const routineIdParam = urlParams.get('routine');
        const dietIdParam = urlParams.get('diet');
        setSharedRoutineFromLinkId(routineIdParam || null);
        setSharedDietFromLinkId(dietIdParam || null);
        if (user) {
          setSelectedUserId(userId);
          setCurrentView("profile");
          setSharedProfileUserId(null);
        } else {
          setSelectedUserId(userId);
          setCurrentView("profile");
          setSharedProfileUserId(userId);
        }
      }
    } else if (urlParams.get('view') && user) {
      setSharedRoutineFromLinkId(null);
      setSharedDietFromLinkId(null);
      const view = urlParams.get('view');
      // ✅ NUEVO: Agregar 'admin', 'diets' y 'premium' a las vistas válidas
      if ([
        'routines', 'diets', 'search', 'myProfile', 'progress', 'welcome', 'admin', 'premium', 'politica'
      ].includes(view)) {
        setCurrentView(view);
        if (view === 'routines') {
          setRoutineManagerKey(prev => prev + 1);
        }
      }
    }
  }, [user]);

  // ✅ OPTIMIZACIÓN: Inicialización de usuario más eficiente
  const initializeUserInFirestore = useCallback(async () => {
    if (!user || initializationRef.current) return;

    initializationRef.current = true;

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // No usar displayName de Auth si es igual al email (algunos proveedores lo asignan así)
        const authDisplayName = user.displayName && user.email &&
          user.displayName.trim().toLowerCase() === user.email.trim().toLowerCase()
          ? null
          : (user.displayName || null);
        const initialData = {
          displayName: authDisplayName,
          email: user.email,
          photoURL: user.photoURL || "",
          customPhotoURL: null,
          status: "¡Nuevo en Ratafit! 💪",
          createdAt: new Date(),
          lastLogin: new Date(),
          hasSeenMatrixWelcome: false,
          isBlocked: false
        };

        await setDoc(userRef, initialData);
        setUserProfileData(initialData);
      } else {
        await setDoc(userRef, {
          lastLogin: new Date()
        }, { merge: true });

        const userData = userSnap.data();
        setUserProfileData(userData);
      }

      // ✅ NUEVO: Verificar status de bloqueo y admin después de cargar datos
      await checkUserBlockStatus();
      await checkAdminStatus();

    } catch (error) {
      console.error("Error inicializando usuario:", error);
    } finally {
      // ✅ NUEVO: Marcar como inicializado después de cargar datos
      setIsInitialized(true);
    }
  }, [user, checkUserBlockStatus, checkAdminStatus]);

  // ✅ OPTIMIZACIÓN: Effect principal de inicialización
  useEffect(() => {
    if (loading) return; // No hacer nada mientras está cargando

    if (user) {
      initializeUserInFirestore();
      // ✅ NO limpiar sharedProfileUserId inmediatamente
    } else {
      setUserProfileData(null);
      setIsAdmin(false);
      setIsUserBlocked(false);
      setBlockInfo(null);
      initializationRef.current = false;
      setIsInitialized(true); // Para usuarios no logueados
    }
  }, [user, loading, initializeUserInFirestore]);

  // ✅ OPTIMIZACIÓN: Effect de routing separado
  useEffect(() => {
    if (loading) return; // No manejar routing durante carga

    handleRouting();

    const handlePopState = () => {
      if (!loading) {
        handleRouting();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user, loading, handleRouting]);

  // Solicitar permiso de notificaciones push y guardar token FCM (solo en navegadores soportados)
  useEffect(() => {
    if (!user || !isInitialized) return;
    if (!canUseMessaging()) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
    if (/Instagram|FBAN|FBAV|Line|Snapchat|Twitter|TikTok|WeChat/i.test(ua)) return;

    // Usar el mismo SW que controla la página (service-worker.js) para FCM; así el push llega aquí y muestra nuestra notificación
    const getMainSwRegistration = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (reg) return reg;
        await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
        return navigator.serviceWorker.ready;
      } catch (_) {
        return undefined;
      }
    };

    const requestAndSaveToken = async () => {
      try {
        const messaging = await getMessagingSafe();
        if (!messaging) return;
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        const registration = await getMainSwRegistration();
        const { getToken, onMessage } = await import('firebase/messaging');
        const fcmToken = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: registration
        });
        if (fcmToken) {
          const userRef = doc(db, "users", user.uid);
          await setDoc(userRef, { fcmToken }, { merge: true });
        }
        // Mostrar notificación cuando llega un push con la pestaña abierta (primer plano)
        onMessage(messaging, (payload) => {
          const title = payload.notification?.title || payload.data?.title || 'Ratafit';
          const body = payload.notification?.body || payload.data?.body || '';
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification(title, { body, icon: '/logo192.png' });
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.info('FCM no disponible:', err?.message || err);
        }
      }
    };

    if (Notification.permission === 'default') {
      const delayMs = 5000;
      const timer = setTimeout(() => requestAndSaveToken(), delayMs);
      return () => clearTimeout(timer);
    }
    if (Notification.permission === 'granted') {
      const t = setTimeout(() => requestAndSaveToken(), 1000);
      return () => clearTimeout(t);
    }
  }, [user, isInitialized]);

  // Actualizar URL cuando cambia la vista - ✅ NUEVO: Incluir admin
  const updateURL = useCallback((view, userId = null) => {
    let newURL = window.location.origin;

    if (view === "profile" && userId) {
      // Mantener posibles parámetros ?routine=... y ?diet=... al navegar al perfil
      const params = new URLSearchParams(window.location.search);
      const routineId = params.get('routine');
      const dietId = params.get('diet');
      newURL += `/profile/${userId}`;
      const q = [];
      if (routineId) q.push(`routine=${routineId}`);
      if (dietId) q.push(`diet=${dietId}`);
      if (q.length) newURL += '?' + q.join('&');
    } else if (view === "welcome") {
      newURL += "/";
    } else if (view === "politica") {
      // Ruta pública dedicada para la política de privacidad
      newURL += "/politica-de-privacidad";
    } else if (view === "admin") {
      newURL += "/admin";
    } else if (view !== "routines" && user) {
      newURL += `?view=${view}`;
    }

    if (newURL !== window.location.href) {
      window.history.pushState({}, '', newURL);
    }
  }, [user]);

  // Recargar datos del perfil cuando se actualice
  const loadUserProfileData = useCallback(async () => {
    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        setUserProfileData(userSnap.data());
      }
    } catch (error) {
      console.error("Error cargando datos del perfil:", error);
    }
  }, [user]);

  // Effect para recargar perfil cuando sea necesario
  useEffect(() => {
    if (user && currentView === "myProfile" && isInitialized) {
      loadUserProfileData();
    }
  }, [currentView, user, loadUserProfileData, isInitialized]);

  // Modal de perfil físico: solo en pestaña Dietas o Rutinas; al segundo si no tiene datos y no lo cerró con "no volver a mostrar"
  const userId = user?.uid;
  const isDietOrRoutinesView = currentView === "diets" || currentView === "routines";
  useEffect(() => {
    if (!userId || isUserBlocked) return;
    if (getFitnessBannerDismissed()) return;
    if (!isDietOrRoutinesView) return;
    if (!navigator.onLine) return;

    const timer = setTimeout(async () => {
      try {
        const userRef = doc(db, "users", userId);
        const snap = await getDoc(userRef);
        const fp = snap.exists() ? (snap.data().fitnessProfile || {}) : {};
        const hasAny = [fp.age, fp.sex, fp.weightKg, fp.heightCm, fp.bodyType, fp.experienceYears].some(
          (v) => v != null && v !== "" && String(v).trim() !== ""
        );
        if (!hasAny) setShowFitnessBanner(true);
      } catch (_) { }
    }, 1000);

    return () => clearTimeout(timer);
  }, [userId, isUserBlocked, isDietOrRoutinesView]);

  // Actualizar URL cuando cambian vista o usuario seleccionado
  useEffect(() => {
    if (isInitialized) {
      updateURL(currentView, selectedUserId);
    }
  }, [currentView, selectedUserId, updateURL, isInitialized]);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Cerrar menú al cambiar vista
  useEffect(() => {
    setIsMenuOpen(false);
  }, [currentView]);

  // ✅ NUEVO: Función para ir a la página de bienvenida
  const handleGoToWelcome = useCallback(() => {
    setCurrentView("welcome");
    setSelectedUserId(null);
    setIsMenuOpen(false);
    // Actualizar la URL a / para que isPrivacyRoute sea false y se muestre Welcome (no Política)
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', window.location.origin + '/');
    }
  }, []);

  // ✅ NUEVO: Función para ir al dashboard de admin
  const handleGoToAdmin = () => {
    if (isAdmin) {
      setCurrentView("admin");
      setSelectedUserId(null);
      setIsMenuOpen(false);
    }
  };

  // Al estar en welcome con usuario logueado: detectar entrenamiento iniciado o única rutina para el CTA
  useEffect(() => {
    if (!user || currentView !== "welcome") return;
    let cancelled = false;

    (async () => {
      try {
        const uid = user.uid;
        let hasActiveWorkout = false;
        let destination = null;

        try {
          // Buscar el último entrenamiento modificado (cualquier estado).
          // Usar getDocsFromServer para evitar caché: así el botón "Continuar con el entrenamiento iniciado" se actualiza correctamente.
          const logsRef = collection(db, "users", uid, "workoutLogs");
          const q = query(logsRef, orderBy("lastModified", "desc"), limit(1));
          const snap = await getDocsFromServer(q);

          if (!cancelled && !snap.empty) {
            const d = snap.docs[0].data();
            const status = d.status;
            const routineId = d.routineId || null;
            const dayName = d.dayName || null;

            if ((status === "active" || status === "paused") && routineId && dayName) {
              hasActiveWorkout = true;
              destination = { routineId, dayName };
            }
          }
        } catch (err) {
          console.error("Error leyendo último workoutLog:", err);
        }

        if (cancelled) return;

        // Si no hay entrenamiento activo/pausado, comprobar si solo hay una rutina
        if (!destination) {
          const routinesRef = collection(db, "users", uid, "routines");
          const routinesSnap = await getDocs(routinesRef);
          const list = routinesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          if (list.length === 1) {
            destination = { routineId: list[0].id };
          }
        }

        if (!cancelled) {
          setContinueData({ hasActiveWorkout, destination });
        }
      } catch (e) {
        console.error("Error calculando destino de continuar:", e);
        if (!cancelled) {
          setContinueData({ hasActiveWorkout: false, destination: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, currentView, welcomeContinueRefreshTrigger]);

  // Al volver a la pestaña en welcome: refrescar estado de entrenamiento (evita que el botón "Continuar entrenamiento" no se actualice por caché)
  useEffect(() => {
    if (!user || currentView !== "welcome") return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setWelcomeContinueRefreshTrigger((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [user, currentView]);

  // ✅ HÍBRIDO: Función para comenzar desde la página de bienvenida
  const handleGetStarted = async (destination = null) => {
    if (user) {
      setSelectedRoutineId(destination?.routineId ?? null);
      setSelectedDayName(destination?.dayName ?? null);
      setCurrentView("routines");
      setRoutineManagerKey(prev => prev + 1);
    } else {
      try {
        await contextSignInWithGoogle();
      } catch (err) {
        console.error("Error al iniciar sesión:", err);
        alert("Error al iniciar sesión. Por favor, intenta de nuevo.");
      }
    }
  };

  // ✅ NUEVO: Función para refrescar estado de bloqueo
  const refreshBlockStatus = useCallback(async () => {
    await checkUserBlockStatus();
  }, [checkUserBlockStatus]);

  // ✅ NUEVO: Función para formatear fechas para BlockedUserScreen (robusta por región)
  const formatDate = (date) => {
    if (!date) return 'N/A';

    try {
      let dateObj;

      if (date instanceof Date) {
        dateObj = date;
      } else if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else {
        return 'Fecha inválida';
      }

      if (isNaN(dateObj.getTime())) {
        return 'Fecha inválida';
      }
      const locale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'es';
      try {
        return new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(dateObj);
      } catch (_) {
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(dateObj.getDate())}/${pad(dateObj.getMonth() + 1)}/${dateObj.getFullYear()} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
      }

    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Error en fecha';
    }
  };

  // ✅ HÍBRIDO: Función de login (usa One Tap o popup desde el contexto); navegación tras login vía useEffect
  const signInWithGoogle = async () => {
    try {
      await contextSignInWithGoogle();
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      const isApp = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
      if (isApp) {
        alert("No se pudo completar el inicio de sesión. En emuladores suele fallar: prueba en un móvil real o en www.ratafit.com desde el navegador.");
      } else {
        alert("Error al iniciar sesión. Por favor, intenta de nuevo.");
      }
    }
  };

  /** Bienvenida: escritorio = solo popup (CRT estable); móvil/PWA = redirect vía contexto */
  const signInWithGoogleOnWelcome = async () => {
    try {
      await contextSignInWithGoogleForWelcome();
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      const isApp = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
      if (isApp) {
        alert("No se pudo completar el inicio de sesión. En emuladores suele fallar: prueba en un móvil real o en www.ratafit.com desde el navegador.");
      } else {
        alert("Error al iniciar sesión. Por favor, intenta de nuevo.");
      }
    }
  };

  // Navegar al perfil compartido cuando el usuario inicie sesión con un enlace de perfil
  useEffect(() => {
    if (user && sharedProfileUserId) {
      setSelectedUserId(sharedProfileUserId);
      setCurrentView("profile");
      setSharedProfileUserId(null);
    }
  }, [user, sharedProfileUserId]);

  const applyPostSignOutState = useCallback(() => {
    setCurrentView("welcome");
    setSelectedUserId(null);
    setIsMenuOpen(false);
    setUserProfileData(null);
    setSharedProfileUserId(null);
    setIsAdmin(false);
    setIsUserBlocked(false);
    setBlockInfo(null);
    setIsInitialized(false);
    initializationRef.current = false;
    setRoutineManagerKey((prev) => prev + 1);
    window.history.pushState({}, '', window.location.origin);
    /* Navigation puede haber dejado el logo del héroe en “desaparecido” sin onLogoAppear al desmontarse */
    setLogoDisappearing(false);
    setLogoGone(false);
    setLogoAppearing(false);
  }, []);

  const handleSignOutCrtAnimationEnd = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    const name = (e && e.animationName) || '';
    if (!name.includes('ratafit-crt-poweroff')) return;
    if (signOutCrtAnimHandledRef.current) return;
    signOutCrtAnimHandledRef.current = true;
    setSignOutCrtPhase('signingOut');
    (async () => {
      try {
        await signOut(auth);
        applyPostSignOutState();
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
      } finally {
        setSignOutCrtPhase('idle');
      }
    })();
  }, [applyPostSignOutState]);

  const handleSignOut = useCallback(() => {
    if (!user || signOutCrtPhase !== 'idle') return;
    signOutCrtAnimHandledRef.current = false;
    setSignOutCrtSurfaceKey((k) => k + 1);
    setSignOutCrtPhase('animating');
  }, [user, signOutCrtPhase]);

  const handleViewProfile = (userId = null) => {
    setSelectedUserId(userId);
    setCurrentView("profile");
  };

  const handleBackFromProfile = () => {
    setSelectedUserId(null);
    setCurrentView("search");
  };

  const handleAvatarClick = () => {
    setCurrentView("myProfile");
    setIsMenuOpen(false);
  };

  // 🆕 FUNCIONES DE NAVEGACIÓN PARA NOTIFICACIONES
  const handleNavigateToProfile = useCallback((userId) => {
    setSelectedUserId(userId);
    setCurrentView("profile");
    setIsMenuOpen(false);
  }, []);

  const handleNavigateToRoutine = useCallback((routineId) => {
    // Navegar a la rutina específica
    setSelectedRoutineId(routineId);
    setCurrentView("routines");
    setIsMenuOpen(false);
    // Forzar re-render del RoutineManager para que detecte la nueva rutina seleccionada
    setRoutineManagerKey(prev => prev + 1);
  }, []);

  // Obtener URL compartible del perfil
  const getShareableProfileURL = (userId) => {
    return buildPublicAppUrl(`/profile/${userId}`);
  };

  // Eliminaré renderNavigation y usaré el componente Navigation
  // const renderNavigation = () => { ... }

  // Handlers para Navigation (atajo: tocar Rutinas/Dietas estando ya ahí lleva a la lista principal)
  const handleNavViewChange = (view) => {
    if (![
      "diets", "routines", "progress", "search", "myProfile", "profile", "welcome", "admin", "premium", "politica"
    ].includes(view)) return;
    if (view === "routines" && currentView === "routines") {
      setRoutinesGoToRootTrigger((t) => t + 1);
      return;
    }
    if (view === "diets" && currentView === "diets") {
      setDietsGoToRootTrigger((t) => t + 1);
      return;
    }
    setCurrentView(view);
  };

  // Estado para el logo de Ratafit
  const [logoDisappearing, setLogoDisappearing] = useState(false);
  const [logoGone, setLogoGone] = useState(false);
  const [logoAppearing, setLogoAppearing] = useState(false);

  // Handler para desaparecer el logo
  const handleLogoDisappear = useCallback(() => {
    if (!logoDisappearing && !logoGone) {
      setLogoDisappearing(true);
      setTimeout(() => {
        setLogoGone(true);
      }, 500);
    }
  }, [logoDisappearing, logoGone]);

  // Handler para hacer aparecer el logo
  const handleLogoAppear = useCallback(() => {
    if (logoGone) {
      setLogoGone(false);
      setLogoDisappearing(false);
      setLogoAppearing(true);
      setTimeout(() => {
        setLogoAppearing(false);
      }, 500);
    }
  }, [logoGone]);

  // Notificación global (toast) centrada; se cierra al tocarla
  const showAppNotification = useCallback((message, type = "info") => {
    const notification = document.createElement("div");
    notification.className = `global-notification global-notification--${type}`;
    notification.innerHTML = `
      <div class="global-notification__content">
        <span class="global-notification__text">${message}</span>
      </div>
    `;
    notification.style.zIndex = "99999";
    notification.style.maxWidth = "90vw";
    document.body.appendChild(notification);
    requestAnimationFrame(() => notification.classList.add("global-notification--show"));

    const closeNotification = () => {
      if (!document.body.contains(notification)) return;
      notification.classList.add("global-notification--hide");
      setTimeout(() => {
        if (document.body.contains(notification)) document.body.removeChild(notification);
      }, 200);
    };

    notification.addEventListener("click", closeNotification, { once: true });
    notification.addEventListener("touchstart", closeNotification, { once: true, passive: true });

    setTimeout(closeNotification, 2500);
  }, []);

  // Sincronizar entrenamientos pendientes al cargar la app y al volver online
  useEffect(() => {
    if (!userId || !navigator.onLine) return;
    let lastSyncAt = 0;
    const DEBOUNCE_MS = 3000;
    const runSync = () => {
      if (Date.now() - lastSyncAt < DEBOUNCE_MS) return;
      lastSyncAt = Date.now();
      syncAllPendingWorkouts(userId, (count) => {
        if (count > 0) {
          showAppNotification("Entrenamiento sincronizado con la nube", "success");
        }
      });
    };
    runSync();
    const onOnline = () => runSync();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [userId, showAppNotification]);

  // Copiar URL del perfil al portapapeles y mostrar notificación
  const copyProfileURL = useCallback(async (userId) => {
    try {
      const url = getShareableProfileURL(userId);
      await navigator.clipboard.writeText(url);
      showAppNotification("Enlace de perfil copiado", "success");
    } catch (error) {
      console.error("Error copiando URL:", error);
      const url = getShareableProfileURL(userId);
      try {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        showAppNotification("Enlace de perfil copiado", "success");
      } catch (fallbackError) {
        showAppNotification("No se pudo copiar el enlace", "error");
      }
    }
  }, [showAppNotification]);

  const openAIAssistant = useCallback(() => {
    setAIAssistantMinimizedWhileLoading(false);
    setShowAIAssistant(true);
  }, []);

  const handleGlobalAIDietCreated = useCallback(async (dietId) => {
    if (!user) return;
    try {
      const dietRef = doc(db, "users", user.uid, "diets", dietId);
      const snap = await getDoc(dietRef);
      if (!snap.exists()) {
        showAppNotification("Error al cargar la dieta creada", "error");
        setShowAIAssistant(false);
        return;
      }
      setScrollToDietId(dietId);
      setCurrentView("diets");
      setDietManagerKey((k) => k + 1);
      setShowAIAssistant(false);
      showAppNotification("🎉 Dieta creada con el asistente IA", "success");
    } catch (e) {
      showAppNotification("Error al cargar la dieta creada", "error");
      setShowAIAssistant(false);
    }
  }, [user, showAppNotification]);

  const handleGlobalAIRoutineCreated = useCallback(async (routineId) => {
    if (!user) return;
    try {
      const routineRef = doc(db, "users", user.uid, "routines", routineId);
      const snap = await getDoc(routineRef);
      if (!snap.exists()) {
        showAppNotification("Error al cargar la rutina creada", "error");
        setShowAIAssistant(false);
        return;
      }
      setScrollToRoutineId(routineId);
      setCurrentView("routines");
      setRoutineManagerKey((k) => k + 1);
      setShowAIAssistant(false);
      showAppNotification("🎉 Rutina creada con el asistente IA", "success");
    } catch (e) {
      showAppNotification("Error al cargar la rutina creada", "error");
      setShowAIAssistant(false);
    }
  }, [user, showAppNotification]);

  // Cuando se copia una rutina desde el perfil compartido, navegar a Rutinas y hacer scroll a esa rutina
  const handleRoutineCopiedFromProfile = useCallback((routineId) => {
    if (!user || !routineId) return;
    setSelectedRoutineId(routineId);
    setSelectedDayName(null);
    setScrollToRoutineId(routineId);
    setCurrentView("routines");
    setRoutineManagerKey((k) => k + 1);
  }, [user]);

  // Cuando se copia una dieta desde el perfil compartido, navegar a Dietas y hacer scroll a esa dieta
  const handleDietCopiedFromProfile = useCallback((dietId) => {
    if (!user || !dietId) return;
    setScrollToDietId(dietId);
    setCurrentView("diets");
    setDietManagerKey((k) => k + 1);
  }, [user]);

  const renderContent = () => {
    // ✅ NUEVO: Si el usuario está bloqueado, mostrar BlockedUserScreen
    if (isUserBlocked && blockInfo) {
      return (
        <React.Suspense fallback={<LoadingSpinner />}>
          <BlockedUserScreen
            blockInfo={blockInfo}
            onSignOut={handleSignOut}
            onRefresh={refreshBlockStatus}
            formatDate={formatDate}
          />
        </React.Suspense>
      );
    }

    switch (currentView) {
      case "authApp":
        return <AuthAppPage />;
      case "politica":
        return <PoliticaDePrivacidad />;
      case "welcome":
        return (
          <WelcomePage
            onGetStarted={handleGetStarted}
            continueDestination={continueData.destination}
            hasActiveWorkout={continueData.hasActiveWorkout}
            onSignIn={signInWithGoogleOnWelcome}
            loginInProgress={loginInProgress}
            user={user}
            userProfile={userProfileData}
            logoDisappearing={logoDisappearing}
            logoGone={logoGone}
            logoAppearing={logoAppearing}
            onLogoDisappear={handleLogoDisappear}
            onLogoAppear={handleLogoAppear}
            onNavigateToPolitica={() => handleNavViewChange('politica')}
          />
        );

      case "routines":
        return <RoutineManager
          key={routineManagerKey}
          userId={user.uid}
          selectedRoutineId={selectedRoutineId}
          selectedDayName={selectedDayName}
          onRoutineSelected={() => { setSelectedRoutineId(null); setSelectedDayName(null); }}
          goToRootTrigger={routinesGoToRootTrigger}
          scrollToRoutineId={scrollToRoutineId}
          onScrollTargetConsumed={() => setScrollToRoutineId(null)}
          onNavigateToProfile={handleNavigateToProfile}
          onShowToast={showAppNotification}
        />;

      case "diets":
        return <DietManager key={dietManagerKey} userId={user.uid} goToRootTrigger={dietsGoToRootTrigger} scrollToDietId={scrollToDietId} onScrollTargetConsumed={() => setScrollToDietId(null)} onNavigateToProfile={handleNavigateToProfile} onShowToast={showAppNotification} />;

      case "progress":
        return (
          <div className="content-with-sponsor">
            <ProgressTracker userId={user.uid} />
            <div className="sponsor-sidebar">
              <React.Suspense fallback={null}>
                <SponsorCubeOptimized routineId={user.uid} className="sidebar-sponsor" />
              </React.Suspense>
            </div>
          </div>
        );
      case "premium":
        return <Premium />;

      case "search":
        return (
          <div className="content-with-sponsor">
            <UserSearch onViewProfile={handleViewProfile} onShowToast={showAppNotification} />
            <div className="sponsor-sidebar">
              <React.Suspense fallback={null}>
                <SponsorCubeOptimized routineId={user.uid} className="sidebar-sponsor" />
              </React.Suspense>
            </div>
          </div>
        );

      case "profile":
        return (
          <UserProfile
            userId={selectedUserId}
            onBack={handleBackFromProfile}
            onViewProfile={handleViewProfile}
            onCopyProfileURL={copyProfileURL}
            onSignIn={signInWithGooglePopup}
            onShowToast={showAppNotification}
            initialSharedRoutineId={sharedRoutineFromLinkId}
            initialSharedDietId={sharedDietFromLinkId}
            onRoutineCopied={handleRoutineCopiedFromProfile}
            onDietCopied={handleDietCopiedFromProfile}
            loginInProgress={loginInProgress}
          />
        );

      case "myProfile":
        return (
          <UserProfile
            onBack={() => setCurrentView("routines")}
            onUpdateProfile={loadUserProfileData}
            onViewProfile={handleViewProfile}
            onCopyProfileURL={copyProfileURL}
            onSignIn={signInWithGooglePopup}
            onShowToast={showAppNotification}
            initialSharedRoutineId={null}
            initialSharedDietId={null}
            onRoutineCopied={handleRoutineCopiedFromProfile}
            onDietCopied={handleDietCopiedFromProfile}
            loginInProgress={loginInProgress}
          />
        );

      // ✅ NUEVO: Caso para el dashboard de admin
      case "admin":
        return isAdmin ? <AdminDashboard /> : (
          <div className="admin-access-denied">
            <div className="access-denied-container">
              <h2>🚫 Acceso Denegado</h2>
              <p>No tienes permisos para acceder al dashboard de administrador.</p>
              <button onClick={handleGoToWelcome} className="get-started-button">
                Volver al Inicio
              </button>
            </div>
          </div>
        );

      default:
        return (
          <WelcomePage
            onGetStarted={handleGetStarted}
            continueDestination={continueData.destination}
            hasActiveWorkout={continueData.hasActiveWorkout}
            onSignIn={signInWithGoogleOnWelcome}
            loginInProgress={loginInProgress}
            user={user}
            userProfile={userProfileData}
            logoDisappearing={logoDisappearing}
            logoGone={logoGone}
            logoAppearing={logoAppearing}
            onLogoDisappear={handleLogoDisappear}
            onLogoAppear={handleLogoAppear}
            onNavigateToPolitica={() => handleNavViewChange('politica')}
          />
        );
    }
  };

  // ✅ OPTIMIZACIÓN: Loading screen más específico
  if (loading) {
    return (
      <div className="App">
        <LoadingSpinner text="La paciencia es una virtud, aguarda un momento" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="auth-screen error-screen">
          <div className="error-container">
            <h2>❌ Error de Autenticación</h2>
            <p>Hubo un problema al autenticarte. Por favor, recarga la página e intenta nuevamente.</p>
            <button onClick={() => window.location.reload()}>
              Recargar página
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 🛡️ Ruta siempre accesible: Política de Privacidad
  const isPrivacyRoute = [
    '/politica-de-privacidad',
    '/politicas-de-privacidad'
  ].includes(window.location.pathname);

  return (
    <SponsorProvider>
      <DietsRoutinesCacheProvider>
      <AIAssistantProvider openAssistant={openAIAssistant}>
      <div className="App">
        {/* Mostrar Navigation solo si no estamos en la vista 'welcome' ni en la vista 'admin', o si el usuario está autenticado */}
        {!isUserBlocked && ((currentView !== 'welcome' && currentView !== 'admin') || (user && currentView !== 'admin')) ? (
          <Navigation
            currentView={currentView}
            onViewChange={handleNavViewChange}
            onSignOut={handleSignOut}
            onAvatarClick={handleAvatarClick}
            onGoToAdmin={handleGoToAdmin}
            onGoToWelcome={handleGoToWelcome}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToRoutine={handleNavigateToRoutine}
            onLogoDisappear={handleLogoDisappear}
            onLogoAppear={handleLogoAppear}
            onOpenAIAssistant={openAIAssistant}
          />
        ) : null}
        <main className="app-content">
          <React.Suspense fallback={<LoadingSpinner text="Cargando vista..." />}>
            {isInitialized ? (
              isPrivacyRoute
                ? <PoliticaDePrivacidad />
                : renderContent()
            ) : (
              currentView === 'welcome' ? (
                <WelcomePage
                  onGetStarted={handleGetStarted}
                  continueDestination={continueData.destination}
                  hasActiveWorkout={continueData.hasActiveWorkout}
                  onSignIn={signInWithGoogleOnWelcome}
                  loginInProgress={loginInProgress}
                  user={user}
                  userProfile={userProfileData}
                  logoDisappearing={logoDisappearing}
                  logoGone={logoGone}
                  logoAppearing={logoAppearing}
                  onLogoDisappear={handleLogoDisappear}
                  onLogoAppear={handleLogoAppear}
                  onNavigateToPolitica={() => handleNavViewChange('politica')}
                />
              ) : null
            )}
          </React.Suspense>
        </main>
        <React.Suspense fallback={null}>
          <InstallBanner />
        </React.Suspense>
        {user && (
          <FitnessProfileBannerModal
            visible={showFitnessBanner && isDietOrRoutinesView}
            onClose={() => setShowFitnessBanner(false)}
            onDontShowAgain={() => setShowFitnessBanner(false)}
            userId={user.uid}
          />
        )}
        {/* Asistente IA global: visible en toda la app cuando el usuario está logueado */}
        {user && (showAIAssistant || aiAssistantMinimizedWhileLoading) && (
          <div
            className={`app-ai-assistant-overlay${aiAssistantMinimizedWhileLoading ? " app-ai-assistant-overlay--minimized" : ""}`}
            aria-hidden={aiAssistantMinimizedWhileLoading}
            onClick={(e) => {
              // Cerrar al tocar fuera del panel (overlay), pero no cuando se hace click dentro del contenedor del asistente
              if (e.target === e.currentTarget) {
                setShowAIAssistant(false);
                setAIAssistantMinimizedWhileLoading(false);
              }
            }}
          >
            <div
              className="app-ai-assistant-wrap"
              onClick={(e) => {
                // Evitar que los clicks dentro del panel cierren el overlay
                e.stopPropagation();
              }}
            >
              <React.Suspense fallback={<LoadingSpinner />}>
              <AIAssistant
                userId={user.uid}
                mode="both"
                isPremium={!!(userProfileData && userProfileData.isPremium)}
                onDietCreated={handleGlobalAIDietCreated}
                onRoutineCreated={handleGlobalAIRoutineCreated}
                onClose={(isLoading) => {
                  if (isLoading) {
                    setAIAssistantMinimizedWhileLoading(true);
                    setShowAIAssistant(false);
                  } else {
                    setShowAIAssistant(false);
                  }
                }}
                onResponseReady={() => {
                  setAIAssistantMinimizedWhileLoading(false);
                  setShowAIAssistant(true);
                }}
              />
              </React.Suspense>
            </div>
          </div>
        )}
        {(signOutCrtPhase === 'animating' || signOutCrtPhase === 'signingOut') && (
          <div
            className="ratafit-welcome-post-login-overlay"
            role="status"
            aria-live="polite"
            aria-busy={signOutCrtPhase === 'signingOut'}
            aria-label="Cerrando sesión"
          >
            {signOutCrtPhase === 'animating' && (
              <div
                key={signOutCrtSurfaceKey}
                className="ratafit-welcome-post-login-crt-surface ratafit-welcome-post-login-crt-surface--poweroff"
                onAnimationEnd={handleSignOutCrtAnimationEnd}
                onWebkitAnimationEnd={handleSignOutCrtAnimationEnd}
              />
            )}
          </div>
        )}
      </div>
      </AIAssistantProvider>
      </DietsRoutinesCacheProvider>
    </SponsorProvider>
  );
}

export default App;